import { useEffect, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../lib/api.js';

interface CredentialStatus {
  configured: boolean;
  success?: boolean;
  error?: string;
}

interface Account {
  id: string;
  name: string;
  region: string;
  marketplace_ids: string;
  default_marketplace_id: string;
  has_ads_credentials?: number;
  ads_profile_ids?: string | null;
}

interface SyncSummary {
  total: number;
  completed: number;
  failed: number;
  running: number;
  never: number;
  totalRows: number;
  exports: {
    dataSource: string;
    name: string;
    schedule: string;
    syncMode: string;
    status: string;
    rows: number;
    lastSyncAt: string | null;
    error: string | null;
  }[];
}

const REGIONS = [
  { value: 'NA', label: 'North America' },
  { value: 'EU', label: 'Europe' },
  { value: 'FE', label: 'Far East' },
];

const MARKETPLACES: Record<string, { id: string; name: string }[]> = {
  NA: [
    { id: 'ATVPDKIKX0DER', name: 'US' },
    { id: 'A2EUQ1WTGCTBG2', name: 'CA' },
    { id: 'A1AM78C64UM0Y8', name: 'MX' },
    { id: 'A2Q3Y263D00KWC', name: 'BR' },
  ],
  EU: [
    { id: 'A1F83G8C2ARO7P', name: 'UK' },
    { id: 'A1PA6795UKMFR9', name: 'DE' },
    { id: 'A13V1IB3VIYZZH', name: 'FR' },
    { id: 'APJ6JRA9NG5V4', name: 'IT' },
    { id: 'A1RKKUPIHCS9HS', name: 'ES' },
    { id: 'A1805IZSGTT6HS', name: 'NL' },
    { id: 'A2NODRKZP88ZB9', name: 'SE' },
    { id: 'A1C3SOZRARQ6R3', name: 'PL' },
    { id: 'ARBP9OOSHTCHU', name: 'EG' },
    { id: 'A33AVAJ2PDY3EV', name: 'TR' },
    { id: 'A21TJRUUN4KGV', name: 'IN' },
    { id: 'A17E79C6D8DWNP', name: 'SA' },
    { id: 'A2VIGQ35RCS4UG', name: 'AE' },
  ],
  FE: [
    { id: 'A1VC38T7YXB528', name: 'JP' },
    { id: 'A39IBJ37TRP1C6', name: 'AU' },
    { id: 'A19VAU5U5O7RUS', name: 'SG' },
  ],
};

export function Credentials() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);

  // Read OAuth callback messages from query params
  const oauthError = searchParams.get('error');
  const adsConnected = searchParams.get('ads_connected');
  const adsMessage = searchParams.get('ads_message');
  const qbConnected = searchParams.get('qb_connected');
  const oauthAccountId = searchParams.get('accountId');

  // Clear query params after reading
  useEffect(() => {
    if (oauthError || adsConnected || qbConnected) {
      const timeout = setTimeout(() => {
        setSearchParams({}, { replace: true });
      }, 10000);
      return () => clearTimeout(timeout);
    }
  }, [oauthError, adsConnected, qbConnected, setSearchParams]);

  function loadAccounts() {
    api.get<Account[]>('/accounts').then(setAccounts).finally(() => setLoadingAccounts(false));
  }

  useEffect(() => { loadAccounts(); }, []);

  async function handleDeactivate(id: string, name: string) {
    if (!confirm(`Deactivate account "${name}"? This will stop all automations.`)) return;
    await api.delete(`/accounts/${id}`);
    loadAccounts();
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-base font-semibold">Credentials</h2>
        <p className="text-sm text-gray-500">Configure API credentials for all services.</p>
      </div>

      {/* OAuth callback messages */}
      {oauthError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {oauthError}
        </div>
      )}
      {adsConnected && adsMessage && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800">
          {adsMessage}
        </div>
      )}
      {qbConnected && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800">
          QuickBooks Online connected successfully.
        </div>
      )}

      <SecretExpiryBanner />

      {/* ── Global Services ─────────────────────────────────────────────── */}
      <section>
        <h3 className="mb-3 text-sm font-semibold text-gray-900">Global Services</h3>
        <p className="mb-4 text-xs text-gray-500">These credentials are shared across all Amazon accounts.</p>
        <div className="space-y-4">
          <DataForSeoSection />
          <KeepaSection />
          <QuickBooksConfigSection />
        </div>
      </section>

      {/* ── Amazon Accounts + Per-Account Credentials ───────────────────── */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Amazon Accounts</h3>
            <p className="text-xs text-gray-500">Manage accounts and their API credentials.</p>
          </div>
          <button
            onClick={() => setShowAddForm(!showAddForm)}
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
          >
            {showAddForm ? 'Cancel' : 'Add Account'}
          </button>
        </div>

        {showAddForm && (
          <div className="mb-6">
            <AddAccountForm onCreated={() => { setShowAddForm(false); loadAccounts(); }} />
          </div>
        )}

        {loadingAccounts ? (
          <div className="h-24 animate-pulse rounded-xl bg-gray-200" />
        ) : accounts.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-300 p-6 text-center">
            <p className="text-sm text-gray-500">No Amazon accounts yet. Click "Add Account" to get started.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {accounts.map((account) => (
              <div key={account.id}>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-gray-900">
                    {account.name}
                    <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">
                      {account.region}
                    </span>
                  </h4>
                  <button
                    onClick={() => handleDeactivate(account.id, account.name)}
                    className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50"
                  >
                    Deactivate
                  </button>
                </div>
                <MarketplaceSelector account={account} onUpdated={loadAccounts} />
                <SyncStatusSection accountId={account.id} />
                <div className="space-y-4">
                  <SpApiSection accountId={account.id} />
                  <AdsApiSection
                    accountId={account.id}
                    key={adsConnected && oauthAccountId === account.id ? 'refreshed' : 'normal'}
                  />
                  <QBConnectionSection accountId={account.id} />
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

// ── SP-API Secret Rotation Banner (page-level warning) ──────────────────────

interface RotationOverviewItem {
  accountId: string;
  name: string;
  rotationDeadline: string;
  notifyDaysBefore: number;
  daysLeft: number;
}

function SecretExpiryBanner() {
  const [items, setItems] = useState<RotationOverviewItem[]>([]);

  useEffect(() => {
    api.get<RotationOverviewItem[]>('/credentials/sp-api/rotation-overview')
      .then(setItems)
      .catch(() => setItems([]));
  }, []);

  // Only warn for accounts inside their notify window (or already expired).
  const due = items.filter((i) => i.daysLeft <= i.notifyDaysBefore);
  if (due.length === 0) return null;

  const expired = due.some((i) => i.daysLeft < 0);
  const tone = expired
    ? 'border-red-300 bg-red-50 text-red-800'
    : 'border-amber-300 bg-amber-50 text-amber-800';

  return (
    <div className={`rounded-lg border p-3 text-sm ${tone}`}>
      <p className="font-semibold">
        {expired ? '🛑 SP-API secret rotation overdue' : '⚠️ SP-API secret rotation coming up'}
      </p>
      <ul className="mt-1 space-y-0.5">
        {due.map((i) => (
          <li key={i.accountId}>
            <strong>{i.name}</strong>:{' '}
            {i.daysLeft < 0
              ? `expired ${-i.daysLeft}d ago (${i.rotationDeadline.slice(0, 10)})`
              : `rotate within ${i.daysLeft}d (by ${i.rotationDeadline.slice(0, 10)})`}
          </li>
        ))}
      </ul>
      <p className="mt-1 text-xs opacity-80">
        Rotate the LWA client secret in Developer Console, then update it under the account's SP-API section below.
      </p>
    </div>
  );
}

// ── Global DataForSEO ──────────────────────────────────────────────────────

function DataForSeoSection() {
  const [status, setStatus] = useState<CredentialStatus | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);

  async function checkStatus() {
    try {
      const s = await api.get<CredentialStatus>('/credentials/dataforseo/status');
      setStatus(s);
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { checkStatus(); }, []);

  return (
    <CredentialCard
      title="DataForSEO API"
      description="Keyword Intelligence — search volume, reverse ASIN, rank tracking. Shared across all accounts."
      status={loading ? 'loading' : status?.configured ? (status.success !== false ? 'connected' : 'error') : 'not_set'}
      error={status?.error}
      showForm={showForm}
      onToggleForm={() => setShowForm(!showForm)}
    >
      <DataForSeoForm onSaved={() => { setShowForm(false); checkStatus(); }} />
    </CredentialCard>
  );
}

function DataForSeoForm({ onSaved }: { onSaved: () => void }) {
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      await api.put('/credentials/dataforseo', { login, password });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <Field label="Login (email)" value={login} onChange={setLogin} placeholder="your-email@example.com" />
      <Field label="API Password" value={password} onChange={setPassword} type="password" placeholder="Your DataForSEO API password" />
      <p className="text-xs text-gray-400">
        Get credentials at <a href="https://app.dataforseo.com/api-access" target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">app.dataforseo.com/api-access</a>. Pay-per-use, ~$0.01/request.
      </p>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <button type="submit" disabled={saving} className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50">
        {saving ? 'Saving...' : 'Save & Test'}
      </button>
    </form>
  );
}

// ── Global Keepa ───────────────────────────────────────────────────────────

function KeepaSection() {
  const [status, setStatus] = useState<CredentialStatus | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);

  async function checkStatus() {
    try {
      const s = await api.get<CredentialStatus>('/credentials/keepa/status');
      setStatus(s);
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { checkStatus(); }, []);

  return (
    <CredentialCard
      title="Keepa API"
      description="Competitive Radar (Module E). Shared across all accounts."
      status={loading ? 'loading' : status?.configured ? 'connected' : 'not_set'}
      showForm={showForm}
      onToggleForm={() => setShowForm(!showForm)}
    >
      <KeepaForm onSaved={() => { setShowForm(false); checkStatus(); }} />
    </CredentialCard>
  );
}

function KeepaForm({ onSaved }: { onSaved: () => void }) {
  const [apiKey, setApiKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      await api.put('/credentials/keepa', { apiKey });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <Field label="API Key" value={apiKey} onChange={setApiKey} type="password" placeholder="Your Keepa API key" />
      {error && <p className="text-sm text-red-600">{error}</p>}
      <button type="submit" disabled={saving} className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50">
        {saving ? 'Saving...' : 'Save'}
      </button>
    </form>
  );
}

// ── Global QuickBooks OAuth Config ────────────────────────────────────────

function QuickBooksConfigSection() {
  const [status, setStatus] = useState<{ configured: boolean; clientId?: string; sandbox?: boolean } | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);

  async function checkStatus() {
    try {
      const s = await api.get<{ configured: boolean; clientId?: string; sandbox?: boolean }>('/qb/config');
      setStatus(s);
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { checkStatus(); }, []);

  return (
    <CredentialCard
      title="QuickBooks Online"
      description={`Financial Brain — auto-sync settlements as journal entries.${status?.sandbox ? ' (Sandbox)' : ''}`}
      status={loading ? 'loading' : status?.configured ? 'connected' : 'not_set'}
      showForm={showForm}
      onToggleForm={() => setShowForm(!showForm)}
    >
      <QuickBooksConfigForm onSaved={() => { setShowForm(false); checkStatus(); }} />
    </CredentialCard>
  );
}

function QuickBooksConfigForm({ onSaved }: { onSaved: () => void }) {
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [sandbox, setSandbox] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const MCP_URL = import.meta.env.VITE_API_URL ?? 'https://sellvana-mcp.vandreus.workers.dev';

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      await api.put('/qb/config', { clientId, clientSecret, sandbox });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <Field label="Client ID" value={clientId} onChange={setClientId} placeholder="Your Intuit Client ID" />
      <Field label="Client Secret" value={clientSecret} onChange={setClientSecret} type="password" placeholder="Your Intuit Client Secret" />
      <label className="flex items-center gap-2 text-sm text-gray-700">
        <input type="checkbox" checked={sandbox} onChange={(e) => setSandbox(e.target.checked)} className="rounded border-gray-300" />
        Sandbox mode (use development keys)
      </label>
      <p className="text-xs text-gray-400">
        Create an app at{' '}
        <a href="https://developer.intuit.com" target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">developer.intuit.com</a>.
        Set redirect URI to: <code className="rounded bg-gray-100 px-1 py-0.5">{MCP_URL}/api/qb/callback</code>
      </p>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <button type="submit" disabled={saving} className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50">
        {saving ? 'Saving...' : 'Save'}
      </button>
    </form>
  );
}

// ── Per-Account QuickBooks Connection ────────────────────────────────────

function QBConnectionSection({ accountId }: { accountId: string }) {
  const [status, setStatus] = useState<{ connected: boolean; companyName?: string | null; realmId?: string } | null>(null);
  const [qbConfigured, setQbConfigured] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [disconnecting, setDisconnecting] = useState(false);

  async function checkStatus() {
    try {
      const connStatus = await api.get<{ connected: boolean; companyName?: string | null; realmId?: string }>(`/finance/accounting/${accountId}`);
      setStatus(connStatus);
    } catch {
      setStatus({ connected: false });
    }
    try {
      const configStatus = await api.get<{ configured: boolean }>('/qb/config');
      setQbConfigured(configStatus.configured);
    } catch {
      // ignore
    }
    setLoading(false);
  }

  useEffect(() => { checkStatus(); }, [accountId]);

  async function handleDisconnect() {
    setDisconnecting(true);
    try {
      await api.delete(`/finance/accounting/${accountId}`);
      checkStatus();
    } catch {
      // ignore
    } finally {
      setDisconnecting(false);
    }
  }

  const MCP_URL = import.meta.env.VITE_API_URL ?? 'https://sellvana-mcp.vandreus.workers.dev';

  return (
    <CredentialCard
      title="QuickBooks Connection"
      description="Connect this account to QuickBooks Online for settlement sync."
      status={loading ? 'loading' : status?.connected ? 'connected' : 'not_set'}
      showForm={showForm}
      onToggleForm={() => setShowForm(!showForm)}
    >
      <div className="space-y-3">
        {status?.connected ? (
          <>
            <div className="flex items-center gap-3 text-sm">
              <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700">Connected</span>
              {status.companyName && <span className="font-medium">{status.companyName}</span>}
              {status.realmId && <span className="text-xs text-gray-400">Realm: {status.realmId}</span>}
            </div>
            <div className="flex items-center gap-2">
              <a
                href={`${MCP_URL}/api/qb/auth?accountId=${accountId}`}
                className="inline-flex items-center rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
              >
                Reconnect
              </a>
              <button
                onClick={handleDisconnect}
                disabled={disconnecting}
                className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
              >
                {disconnecting ? 'Disconnecting...' : 'Disconnect'}
              </button>
            </div>
          </>
        ) : qbConfigured ? (
          <div>
            <p className="mb-2 text-xs text-gray-500">
              Click below to authorize QuickBooks access for this account.
            </p>
            <a
              href={`${MCP_URL}/api/qb/auth?accountId=${accountId}`}
              className="inline-flex items-center rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
            >
              Connect QuickBooks
            </a>
          </div>
        ) : (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
            <p className="text-xs text-amber-800">
              <strong>First:</strong> Configure QuickBooks OAuth credentials in the Global Services section above.
            </p>
          </div>
        )}
      </div>
    </CredentialCard>
  );
}

// ── Per-Account Sync Status ──────────────────────────────────────────────

function SyncStatusSection({ accountId }: { accountId: string }) {
  const [summary, setSummary] = useState<SyncSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  async function loadSummary() {
    try {
      const s = await api.get<SyncSummary>(`/sync/summary?account_id=${accountId}`);
      setSummary(s);
    } catch {
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadSummary(); }, [accountId]);

  async function handleSyncAll() {
    setSyncing(true);
    try {
      await api.post('/sync/all', { accountId });
      setTimeout(loadSummary, 5000);
    } catch {
      // ignore
    } finally {
      setSyncing(false);
    }
  }

  if (loading) return <div className="h-16 animate-pulse rounded-xl bg-gray-100 mb-4" />;
  if (!summary || summary.total === 0) return null;

  const pct = summary.total > 0 ? Math.round((summary.completed / summary.total) * 100) : 0;

  return (
    <div className="mb-4 rounded-xl border border-gray-200 bg-white p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <h5 className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Data Sync</h5>
          <span className="text-xs text-gray-500">
            {summary.totalRows.toLocaleString()} rows across {summary.total} exports
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="rounded-lg border border-gray-200 px-3 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50"
          >
            {showDetails ? 'Hide' : 'Details'}
          </button>
          <button
            onClick={handleSyncAll}
            disabled={syncing}
            className="rounded-lg bg-brand-600 px-3 py-1 text-xs font-medium text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {syncing ? 'Syncing...' : 'Sync All'}
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="flex items-center gap-3">
        <div className="flex-1 h-2 rounded-full bg-gray-100 overflow-hidden">
          <div
            className="h-full rounded-full bg-green-500 transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="flex items-center gap-2 text-xs">
          {summary.completed > 0 && <span className="text-green-600">{summary.completed} ok</span>}
          {summary.running > 0 && <span className="text-blue-600">{summary.running} running</span>}
          {summary.failed > 0 && <span className="text-red-600">{summary.failed} failed</span>}
          {summary.never > 0 && <span className="text-gray-400">{summary.never} pending</span>}
        </div>
      </div>

      {/* Detailed export list */}
      {showDetails && (
        <div className="mt-3 border-t border-gray-100 pt-3">
          <div className="grid grid-cols-[1fr_80px_70px_60px] gap-1 text-xs">
            <span className="font-medium text-gray-500">Export</span>
            <span className="font-medium text-gray-500">Schedule</span>
            <span className="font-medium text-gray-500 text-right">Rows</span>
            <span className="font-medium text-gray-500 text-right">Status</span>
            {summary.exports.map((exp) => (
              <div key={exp.dataSource + exp.name} className="contents">
                <span className="text-gray-700 truncate" title={exp.name}>{exp.name}</span>
                <span className="text-gray-400">{exp.schedule}</span>
                <span className="text-gray-600 text-right">{exp.rows.toLocaleString()}</span>
                <span className={`text-right font-medium ${
                  exp.status === 'completed' ? 'text-green-600' :
                  exp.status === 'running' ? 'text-blue-600' :
                  exp.status === 'failed' ? 'text-red-600' : 'text-gray-400'
                }`}>
                  {exp.status === 'completed' ? 'OK' : exp.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Per-Account SP-API ─────────────────────────────────────────────────────

interface SecretMeta {
  rotationDeadline: string | null;
  rotatedAt: string | null;
  notifyDaysBefore: number;
  daysLeft: number | null;
}

interface SpApiStatus {
  configured: boolean;
  success?: boolean;
  lwaOk?: boolean;
  authOk?: boolean;
  diagnosis?: string;
  error?: string;
  marketplaces?: string[];
  secret?: SecretMeta;
}

function SpApiSection({ accountId }: { accountId: string }) {
  const [status, setStatus] = useState<SpApiStatus | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);

  async function checkStatus() {
    setLoading(true);
    try {
      const s = await api.get<SpApiStatus>(`/credentials/sp-api/${accountId}/status`);
      setStatus(s);
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { checkStatus(); }, [accountId]);

  const cardStatus = loading
    ? 'loading'
    : !status?.configured
      ? 'not_set'
      : status.authOk
        ? 'connected'
        : 'error';

  // When configured but unhealthy, surface WHY (token vs. data access).
  const errLine = !loading && status?.configured && !status.authOk
    ? status.lwaOk
      ? `Token works but data access denied — ${status.diagnosis ?? status.error ?? 'app may be blocked'}`
      : status.error ?? 'Connection error'
    : undefined;

  return (
    <CredentialCard
      title="SP-API (Selling Partner API)"
      description="Orders, inventory, listings, finance, reports."
      status={cardStatus}
      error={errLine}
      extra={<SecretExpiry secret={status?.secret} />}
      showForm={showForm}
      onToggleForm={() => setShowForm(!showForm)}
    >
      <SpApiForms accountId={accountId} secret={status?.secret} onSaved={() => { checkStatus(); }} />
    </CredentialCard>
  );
}

// Always-visible secret rotation countdown chip.
function SecretExpiry({ secret }: { secret?: SecretMeta }) {
  if (!secret) return null;
  if (secret.rotationDeadline == null || secret.daysLeft == null) {
    return (
      <p className="mt-1.5 text-xs text-gray-400">
        Secret rotation date not tracked yet — save/rotate the secret below to start the countdown.
      </p>
    );
  }
  const d = secret.daysLeft;
  const color = d < 0
    ? 'bg-red-100 text-red-700'
    : d <= secret.notifyDaysBefore
      ? 'bg-amber-100 text-amber-700'
      : 'bg-green-100 text-green-700';
  const label = d < 0 ? `Secret EXPIRED ${-d}d ago` : `Secret rotates in ${d}d`;
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-2">
      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${color}`}>{label}</span>
      <span className="text-xs text-gray-400">
        deadline {secret.rotationDeadline.slice(0, 10)} · alerts {secret.notifyDaysBefore}d before
      </span>
    </div>
  );
}

function SpApiForms({ accountId, secret, onSaved }: { accountId: string; secret?: SecretMeta; onSaved: () => void }) {
  return (
    <div className="space-y-5">
      <SecretRotateForm accountId={accountId} onSaved={onSaved} />
      <hr className="border-gray-100" />
      <NotifyForm accountId={accountId} current={secret?.notifyDaysBefore ?? 14} onSaved={onSaved} />
      <hr className="border-gray-100" />
      <details className="text-xs text-gray-500">
        <summary className="cursor-pointer hover:text-gray-700">Full credentials (first-time setup / re-authorize)</summary>
        <div className="mt-2">
          <SpApiForm accountId={accountId} onSaved={onSaved} />
        </div>
      </details>
    </div>
  );
}

// Rotate just the client secret (the common, recurring 180-day task).
function SecretRotateForm({ accountId, onSaved }: { accountId: string; onSaved: () => void }) {
  const [clientSecret, setClientSecret] = useState('');
  const [deadline, setDeadline] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState('');

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError('');
    setResult('');
    try {
      const body: { clientSecret: string; rotationDeadline?: string } = { clientSecret };
      if (deadline) body.rotationDeadline = new Date(`${deadline}T00:00:00Z`).toISOString();
      const res = await api.put<{ test?: { authOk?: boolean; diagnosis?: string } }>(
        `/credentials/sp-api/${accountId}/secret`,
        body,
      );
      const t = res?.test;
      setResult(t ? (t.authOk ? '✓ Rotated — data access confirmed.' : `Rotated, but data still denied: ${t.diagnosis ?? ''}`) : 'Secret rotated.');
      setClientSecret('');
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <p className="text-xs font-medium text-gray-700">Rotate client secret</p>
      <Field label="New Client Secret" value={clientSecret} onChange={setClientSecret} type="password" placeholder="amzn1.oa2-cs.v1.xxx" />
      <div>
        <label className="block text-xs font-medium text-gray-700">Rotation deadline (optional — defaults to +180 days)</label>
        <input
          type="date"
          value={deadline}
          onChange={(e) => setDeadline(e.target.value)}
          className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        />
      </div>
      <p className="text-xs text-gray-400">
        Developer Console → your app → LWA credentials → <strong>Rotate Secret</strong>. Paste the new secret here and enter the "Date limite de rotation" Amazon shows (or leave blank for +180 days).
      </p>
      {error && <p className="text-sm text-red-600">{error}</p>}
      {result && <p className={`text-sm ${result.startsWith('✓') ? 'text-green-600' : 'text-amber-600'}`}>{result}</p>}
      <button type="submit" disabled={saving} className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50">
        {saving ? 'Rotating...' : 'Rotate & Test'}
      </button>
    </form>
  );
}

function NotifyForm({ accountId, current, onSaved }: { accountId: string; current: number; onSaved: () => void }) {
  const [days, setDays] = useState(String(current));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      await api.put(`/credentials/sp-api/${accountId}/notify`, { notifyDaysBefore: Number(days) });
      setSaved(true);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-2">
      <p className="text-xs font-medium text-gray-700">Rotation reminder</p>
      <div className="flex items-center gap-2">
        <span className="text-xs text-gray-500">Alert me</span>
        <input
          type="number"
          min={0}
          max={180}
          value={days}
          onChange={(e) => setDays(e.target.value)}
          className="w-20 rounded-lg border border-gray-300 px-2 py-1 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        />
        <span className="text-xs text-gray-500">days before the deadline (Telegram + dashboard banner)</span>
        <button type="submit" disabled={saving} className="rounded-lg border border-gray-300 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
          {saving ? 'Saving...' : 'Save'}
        </button>
        {saved && <span className="text-xs text-green-600">✓</span>}
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
    </form>
  );
}

function SpApiForm({ accountId, onSaved }: { accountId: string; onSaved: () => void }) {
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [refreshToken, setRefreshToken] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      await api.put(`/credentials/sp-api/${accountId}`, { clientId, clientSecret, refreshToken });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <Field label="Client ID" value={clientId} onChange={setClientId} placeholder="amzn1.application-oa2-client.xxx" />
      <Field label="Client Secret" value={clientSecret} onChange={setClientSecret} type="password" placeholder="amzn1.oa2-cs.v1.xxx" />
      <Field label="Refresh Token" value={refreshToken} onChange={setRefreshToken} type="password" placeholder="Atzr|xxx" />
      {error && <p className="text-sm text-red-600">{error}</p>}
      <button type="submit" disabled={saving} className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50">
        {saving ? 'Saving...' : 'Save & Test'}
      </button>
    </form>
  );
}

// ── Per-Account Amazon Ads ─────────────────────────────────────────────────

function AdsApiSection({ accountId }: { accountId: string }) {
  const [status, setStatus] = useState<CredentialStatus | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [oauthConfigured, setOauthConfigured] = useState(false);

  async function checkStatus() {
    try {
      const profiles = await api.get<{ connected: boolean; profiles: Record<string, string> }>(`/ads/profiles/${accountId}`);
      setStatus({
        configured: profiles.connected,
        success: profiles.connected,
      });
    } catch {
      setStatus(null);
    }

    try {
      const config = await api.get<{ configured: boolean }>('/ads/config');
      setOauthConfigured(config.configured);
    } catch {
      // ignore
    }

    setLoading(false);
  }

  useEffect(() => { checkStatus(); }, [accountId]);

  const MCP_URL = 'https://sellvana-mcp.vandreus.workers.dev';

  return (
    <CredentialCard
      title="Amazon Advertising API"
      description="PPC Intelligence (Module B)."
      status={loading ? 'loading' : status?.configured ? 'connected' : 'not_set'}
      error={status?.error}
      showForm={showForm}
      onToggleForm={() => setShowForm(!showForm)}
    >
      <div className="space-y-4">
        {oauthConfigured ? (
          <div>
            <p className="mb-2 text-xs text-gray-500">
              Connect via OAuth (recommended) — automatically exchanges tokens and discovers profiles.
            </p>
            <a
              href={`${MCP_URL}/api/ads/auth?accountId=${accountId}`}
              className="inline-flex items-center rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
            >
              {status?.configured ? 'Reconnect via Amazon' : 'Connect via Amazon'}
            </a>
          </div>
        ) : (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
            <p className="text-xs text-amber-800">
              <strong>First:</strong> Save your LWA Client ID &amp; Secret below, then click "Connect via Amazon".
            </p>
          </div>
        )}

        <hr className="border-gray-100" />

        <AdsOAuthConfigForm onSaved={() => { setOauthConfigured(true); checkStatus(); }} />

        <hr className="border-gray-100" />

        <details className="text-xs text-gray-500">
          <summary className="cursor-pointer hover:text-gray-700">Manual entry (advanced)</summary>
          <div className="mt-2">
            <AdsApiForm accountId={accountId} onSaved={() => { setShowForm(false); checkStatus(); }} />
          </div>
        </details>
      </div>
    </CredentialCard>
  );
}

function AdsOAuthConfigForm({ onSaved }: { onSaved: () => void }) {
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await api.put('/ads/config', { clientId, clientSecret });
      setSuccess('OAuth config saved. You can now click "Connect via Amazon".');
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <p className="text-xs font-medium text-gray-700">LWA App Credentials</p>
      <Field label="Client ID" value={clientId} onChange={setClientId} placeholder="amzn1.application-oa2-client.xxx" />
      <Field label="Client Secret" value={clientSecret} onChange={setClientSecret} type="password" placeholder="amzn1.oa2-cs.v1.xxx" />
      {error && <p className="text-sm text-red-600">{error}</p>}
      {success && <p className="text-sm text-green-600">{success}</p>}
      <button type="submit" disabled={saving} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
        {saving ? 'Saving...' : 'Save OAuth Config'}
      </button>
    </form>
  );
}

function AdsApiForm({ accountId, onSaved }: { accountId: string; onSaved: () => void }) {
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [refreshToken, setRefreshToken] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      await api.put(`/credentials/ads-api/${accountId}`, { clientId, clientSecret, refreshToken });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <Field label="Client ID" value={clientId} onChange={setClientId} placeholder="amzn1.application-oa2-client.xxx" />
      <Field label="Client Secret" value={clientSecret} onChange={setClientSecret} type="password" />
      <Field label="Refresh Token" value={refreshToken} onChange={setRefreshToken} type="password" />
      {error && <p className="text-sm text-red-600">{error}</p>}
      <button type="submit" disabled={saving} className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50">
        {saving ? 'Saving...' : 'Save'}
      </button>
    </form>
  );
}

// ── Shared Components ──────────────────────────────────────────────────────

function CredentialCard({
  title,
  description,
  status,
  error,
  extra,
  showForm,
  onToggleForm,
  children,
}: {
  title: string;
  description: string;
  status: 'loading' | 'connected' | 'error' | 'not_set';
  error?: string;
  extra?: React.ReactNode;
  showForm: boolean;
  onToggleForm: () => void;
  children: React.ReactNode;
}) {
  const statusColors = {
    loading: 'bg-gray-100 text-gray-500',
    connected: 'bg-green-100 text-green-700',
    error: 'bg-red-100 text-red-700',
    not_set: 'bg-yellow-100 text-yellow-700',
  };

  const statusLabels = {
    loading: 'Checking...',
    connected: 'Connected',
    error: 'Error',
    not_set: 'Not configured',
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-6">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold">{title}</h3>
            <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusColors[status]}`}>
              {statusLabels[status]}
            </span>
          </div>
          <p className="mt-1 text-xs text-gray-500">{description}</p>
          {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
          {extra}
        </div>
        <button
          onClick={onToggleForm}
          className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50"
        >
          {showForm ? 'Cancel' : status === 'connected' ? 'Update' : 'Configure'}
        </button>
      </div>
      {showForm && <div className="mt-4 border-t border-gray-100 pt-4">{children}</div>}
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-700">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        placeholder={placeholder}
        required
      />
    </div>
  );
}

// ── Marketplace Selector (per-account) ───────────────────────────────────────

function MarketplaceSelector({ account, onUpdated }: { account: Account; onUpdated: () => void }) {
  const currentIds: string[] = JSON.parse(account.marketplace_ids || '[]');
  const allMps = MARKETPLACES[account.region] ?? [];
  const [saving, setSaving] = useState(false);

  async function toggle(mpId: string) {
    const isSelected = currentIds.includes(mpId);
    let newIds: string[];

    if (isSelected) {
      newIds = currentIds.filter((id) => id !== mpId);
      if (newIds.length === 0) return; // can't deselect all
    } else {
      newIds = [...currentIds, mpId];
    }

    // If default is being removed, pick first remaining
    let newDefault = account.default_marketplace_id;
    if (!newIds.includes(newDefault)) {
      newDefault = newIds[0];
    }

    setSaving(true);
    try {
      await api.patch(`/accounts/${account.id}`, { marketplaceIds: newIds, defaultMarketplaceId: newDefault });
      onUpdated();
    } catch {
      // ignore
    } finally {
      setSaving(false);
    }
  }

  async function setDefault(mpId: string) {
    setSaving(true);
    try {
      await api.patch(`/accounts/${account.id}`, { defaultMarketplaceId: mpId });
      onUpdated();
    } catch {
      // ignore
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mb-4 rounded-xl border border-gray-200 bg-white p-4">
      <h5 className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">Marketplaces</h5>
      <div className="flex flex-wrap gap-2">
        {allMps.map((mp) => {
          const isSelected = currentIds.includes(mp.id);
          const isDefault = account.default_marketplace_id === mp.id;
          return (
            <button
              key={mp.id}
              type="button"
              disabled={saving}
              onClick={() => toggle(mp.id)}
              onDoubleClick={() => isSelected && setDefault(mp.id)}
              title={isDefault ? `${mp.name} (default)` : isSelected ? 'Double-click to set as default' : `Enable ${mp.name}`}
              className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-50 ${
                isDefault
                  ? 'bg-brand-600 text-white ring-2 ring-brand-300'
                  : isSelected
                    ? 'bg-brand-100 text-brand-700 ring-1 ring-brand-300'
                    : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
              }`}
            >
              {mp.name}
            </button>
          );
        })}
      </div>
      <p className="mt-1.5 text-xs text-gray-400">Click to toggle. Double-click to set as default.</p>
    </div>
  );
}

// ── Add Account Form ────────────────────────────────────────────────────────

function AddAccountForm({ onCreated }: { onCreated: () => void }) {
  const [name, setName] = useState('');
  const [region, setRegion] = useState('NA');
  const [selectedMarketplaces, setSelectedMarketplaces] = useState<string[]>(['ATVPDKIKX0DER']);
  const [defaultMp, setDefaultMp] = useState('ATVPDKIKX0DER');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  function handleRegionChange(newRegion: string) {
    setRegion(newRegion);
    const first = MARKETPLACES[newRegion]?.[0];
    if (first) {
      setSelectedMarketplaces([first.id]);
      setDefaultMp(first.id);
    }
  }

  function toggleMarketplace(id: string) {
    setSelectedMarketplaces((prev) =>
      prev.includes(id) ? prev.filter((m) => m !== id) : [...prev, id],
    );
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim() || selectedMarketplaces.length === 0) return;

    setSaving(true);
    setError('');

    try {
      await api.post('/accounts', {
        name: name.trim(),
        region,
        marketplaceIds: selectedMarketplaces,
        defaultMarketplaceId: defaultMp || selectedMarketplaces[0],
      });
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create account');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="rounded-xl border border-gray-200 bg-white p-6 space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700">Account Name</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
          placeholder="e.g., My Store NA"
          required
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Region</label>
        <div className="mt-1 flex gap-2">
          {REGIONS.map((r) => (
            <button
              key={r.value}
              type="button"
              onClick={() => handleRegionChange(r.value)}
              className={`rounded-lg px-4 py-2 text-sm font-medium ${
                region === r.value
                  ? 'bg-brand-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Marketplaces</label>
        <div className="mt-1 flex flex-wrap gap-2">
          {(MARKETPLACES[region] ?? []).map((mp) => (
            <button
              key={mp.id}
              type="button"
              onClick={() => toggleMarketplace(mp.id)}
              className={`rounded-lg px-3 py-1.5 text-xs font-medium ${
                selectedMarketplaces.includes(mp.id)
                  ? 'bg-brand-100 text-brand-700 ring-1 ring-brand-300'
                  : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}
            >
              {mp.name}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Default Marketplace</label>
        <select
          value={defaultMp}
          onChange={(e) => setDefaultMp(e.target.value)}
          className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        >
          {selectedMarketplaces.map((id) => {
            const mp = MARKETPLACES[region]?.find((m) => m.id === id);
            return (
              <option key={id} value={id}>
                {mp?.name ?? id}
              </option>
            );
          })}
        </select>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <button
        type="submit"
        disabled={saving}
        className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-50"
      >
        {saving ? 'Creating...' : 'Create Account'}
      </button>
    </form>
  );
}
